'use client';

import * as React from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { SectionHeading } from '@/components/frontend/home/section-heading';
import { Typography } from '@/components/ui/typography';
import { getLocalizedPath, getLocalizedPagePath } from '@/lib/locale-utils';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { Card } from '@/components/ui/card';

interface MLMPlan {
    slug: string;
    name: string;
    description: string;
    image?: string;
}

export function FlexibleMLMPlatform({ initialPlans }: { initialPlans?: MLMPlan[] }) {
    const locale = useLocale();
    const t = useTranslations('home.flexibleMlm');
    const sectionRef = useScrollAnimation({ threshold: 0.15 });
    const [plans, setPlans] = useState<MLMPlan[]>(() => initialPlans?.slice(0, 6) ?? []);
    const [isLoading, setIsLoading] = useState(!initialPlans?.length);

    const content = {
        pillLabel: t('pillLabel'),
        title: t('title'),
        description: t('description'),
        subtitle: t('subtitle'),
        viewAllButton: t('viewAllButton'),
    };

    useEffect(() => {
        if (initialPlans?.length) {
            setPlans(initialPlans.slice(0, 6));
            setIsLoading(false);
            return;
        }
        const fetchPlans = async () => {
            try {
                setIsLoading(true);
                const res = await fetch(`/api/mlm-plans?locale=${locale}&showOnHomePage=true`, {
                    cache: 'no-store',
                });

                if (!res.ok) {
                    throw new Error('Failed to fetch MLM plans');
                }

                const data = await res.json();
                const fetchedPlans = Array.isArray(data) ? data : [];
                setPlans(fetchedPlans.slice(0, 6));
            } catch (error) {
                setPlans([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPlans();
    }, [locale, initialPlans?.length]);

    // Map plans from API to card format - use backend slugs directly
    const plansWithHref = useMemo(() => {
        return plans.map((plan) => {
            const href = getLocalizedPath(plan.slug, locale);
            return {
                ...plan,
                href,
            };
        });
    }, [plans, locale]);

    return (
        <SectionWrapper
            ref={sectionRef.ref}
            variant="default"
            padding="md"
            className="relative overflow-hidden !bg-transparent  after:content-[''] after:absolute after:inset-0 after:bg-gray-50 dark:after:bg-gray-950 after:bg-cover after:bg-center after:-z-10 after:opacity-100"
        >

            {/* Header Section - 2 column grid */}
            <div
                className={`grid md:grid-cols-[70%_30%] items-end mb-14 transition-all duration-700 ${sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                    }`}
            >
                {/* Left Column - Title and Description */}
                <div className="text-left">
                    <SectionHeading
                        pill={content.pillLabel}
                        title={content.title}
                        subtitle={content.description}
                        isVisible={sectionRef.isVisible}
                        align="left"
                        maxWidthClass="max-w-4xl"
                        className="!mb-2"
                    />
                    <Typography variant="p" className="mb-4 text-lg text-gray-600 dark:text-gray-300">
                        {content.subtitle}
                    </Typography>
                </div>
                {/* Right Column - View All Plans Button */}
                <div className="flex items-end md:justify-end mt-10 md:mt-0">
                    <Button
                        href={getLocalizedPagePath('mlm-software-plans', locale)}
                        variant="primary"
                        size="md"
                        showArrow={true}
                        className="md:mb-4"
                    >
                        {content.viewAllButton}
                    </Button>
                </div>
            </div>

            {/* Plans Grid - Top row: 3 boxes, Bottom row: 2 boxes centered */}
            <div className="mb-12 mx-auto">
                {isLoading ? (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <div
                                key={i}
                                className="h-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"
                            />
                        ))}
                    </div>
                ) : plansWithHref.length === 0 ? (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                        <p>{t('noPlansAvailable')}</p>
                    </div>
                ) : (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {plansWithHref.map((plan, index) => {
                            return (
                                <Card
                                    key={plan.slug}
                                    title={plan.name}
                                    description={plan.description}
                                    href={plan.href}
                                    imageSrc={plan.image}
                                    imageHeight="h-48"
                                    imageWidth="w-auto"
                                    imagePadding="py-4"
                                    imageAlt={plan.name}
                                    index={index}
                                    animationDelay={index * 100}
                                    imageLoading={index < 3 ? "eager" : "lazy"}
                                    className="!bg-white border border-gray-200 dark:border-gray-800 dark:bg-gradient-to-b dark:from-gray-950 dark:via-gray-900 dark:to-black"
                                />
                            );
                        })}
                    </div>
                )}
            </div>

        </SectionWrapper>
    );
}
